import React, { createContext, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';

const FeaturePermissionsContext = createContext(null);

export const FeaturePermissionsProvider = ({ children }) => {
  const permissions = useSelector((state) => state.loyalty.admin.permissions);

  // Hook to check if a feature flag is enabled
  const hasFeatureFlag = useCallback((flagName) => {
    if (!permissions?.featureFlags) return false;
    return permissions.featureFlags[flagName] === true;
  }, [permissions]);

  // Hook to check quota limits
  const getQuotaLimit = useCallback((quotaName) => {
    if (!permissions?.quotas) return null;
    return permissions.quotas[quotaName];
  }, [permissions]);

  // Hook to check if quota is exceeded
  const isQuotaExceeded = useCallback((quotaName, currentUsage) => {
    const quota = getQuotaLimit(quotaName);
    if (!quota || !quota.limit) return false;
    return currentUsage >= quota.limit;
  }, [getQuotaLimit]);

  const value = {
    permissions,
    hasFeatureFlag,
    getQuotaLimit,
    isQuotaExceeded,
  };

  return (
    <FeaturePermissionsContext.Provider value={value}>
      {children}
    </FeaturePermissionsContext.Provider>
  );
};

export const useFeaturePermissions = () => {
  const context = useContext(FeaturePermissionsContext);
  if (!context) {
    throw new Error('useFeaturePermissions must be used within a FeaturePermissionsProvider');
  }
  return context;
};

export default FeaturePermissionsContext; 